import { useEffect, useRef, useState, useCallback } from 'react';
import { SeoPanel } from './SeoPanel';
import { MixPanel } from './MixPanel';
import { Timeline } from './Timeline';
import type { FootageLane } from './Timeline';
import { FramePreview } from './FramePreview';
import { Director } from './Director';
import { SOURCING_LABEL } from './CardDefaults';
import { History } from './History';
import { Inspector } from './Inspector';
import { ToolRail, type Tool } from './ToolRail';
import { CanvasLayers } from './CanvasLayers';
import { LayerInspector } from './LayerInspector';
import { ThumbnailPanel } from './ThumbnailPanel';
import { CardComments } from './CardComments';
import { SeriesRow } from './SeriesRow';
import { CardIndexPanel } from './CardIndex';
import { secondsPerLine, startOffsets, sceneStarts, placeLayers, sceneAt, joinAt, layersAt } from '../lib/timeline';
import { tick, sceneElapsed, footageTime, footageMuted, footageOf } from '../lib/playback';
import { AssetTray } from './AssetTray';
import { Collection } from './Collection';
import type { DragAsset } from '../lib/drag';
import type { StoredLayer } from '../lib/timeline';
import { runOfShow } from '../lib/runofshow';
import type { Suggestion } from '../lib/api';
import { api, MOTIONS, SHOTS, LEGACY_TRANSITIONS, transitionLabel, type Brand, type Asset, type CardDetail as Detail, type Turn, type Stage, type Connector, type Source, type Moment, type Finding, type MotionKind, type ShotKind, TAG_PRESETS, socialLine, type FilmRequest, dateStamp, type ShortPick, type Card, type Sourcing } from '../lib/api';

const EMOTIONS: Turn['emotion'][] = ['neutral', 'warm', 'urgent', 'amused', 'serious', 'curious'];
const GESTURES: Turn['gesture'][] = ['none', 'nod', 'lean-in', 'point', 'open-hands', 'count'];

function mmss(s: number): string {
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}

/** A render is the only thing here that takes minutes, so it gets a real state line. */
function RenderState({ d }: { d: Detail }) {
  if (!d.job) return <span className="chip">not rendered</span>;
  const tone: Record<string, string> = {
    complete: 'var(--green)', running: 'var(--amber)', queued: 'var(--ink-3)',
    failed: 'var(--red)', cancelled: 'var(--ink-3)',
  };
  return (
    <span className="chip" style={{ color: tone[d.job.status] }}>
      {d.job.status}
      {d.job.attempts > 1 && d.job.status !== 'complete' ? ` · attempt ${d.job.attempts}` : ''}
    </span>
  );
}


/** Speeds a creator reaches for. The schema allows anything from 0.25 to 4. */
const SPEEDS = [0.5, 0.75, 1.5, 2] as const;

/**
 * Framings, as fractions of the source.
 *
 * A draggable box over the frame preview is the right control and is its own
 * piece of work. These are the reframes people actually ask for, and each one
 * is a rectangle the API and the renderer both accept — which a hand-drawn box
 * would also have to be.
 */
const CROPS: Record<string, Turn['crop']> = {
  'full frame': null,
  'centre half': { x: 0.25, y: 0.25, w: 0.5, h: 0.5 },
  'top half': { x: 0, y: 0, w: 1, h: 0.5 },
  'bottom half': { x: 0, y: 0.5, w: 1, h: 0.5 },
  'left half': { x: 0, y: 0, w: 0.5, h: 1 },
  'right half': { x: 0.5, y: 0, w: 0.5, h: 1 },
  'centre square': { x: 0.25, y: 0, w: 0.5, h: 1 },
};

function cropName(crop: Turn['crop']): string {
  if (!crop) return 'full frame';
  const hit = Object.entries(CROPS).find(([, c]) =>
    c && c.x === crop.x && c.y === crop.y && c.w === crop.w && c.h === crop.h);
  // A rectangle that is not one of these is still shown as itself rather than
  // silently reset to full frame — the API accepts any valid rectangle, and
  // something else may have set one.
  return hit ? hit[0] : 'custom';
}

export function CardDetail({ id, onClose, onChanged, onFilm }: {
  id: string; onClose: () => void; onChanged: (stage: Stage) => void;
  /** P3: film the selected scene on the Record screen. */
  onFilm?: (f: FilmRequest) => void;
}) {
  const [d, setD] = useState<Detail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLine, setSelectedLine] = useState<number | null>(null);
  // The playhead: a moment in the programme, in seconds. The frame, the
  // banners in view and the readout all follow it; ← → step it a second.
  const [playhead, setPlayhead] = useState<number | null>(null);
  // T200: the transport. A creator should not have to buy a render to find out
  // whether the timing works, so the playhead can run on its own clock and
  // everything that follows it — the footage, the banners, the bed, the
  // readout — moves with it.
  const [playing, setPlaying] = useState(false);
  // Signed media for a source, fetched once and kept for the session. A clip
  // is asked for the first time it is reached and never again.
  const media = useRef<Map<string, string>>(new Map());
  const [mediaReady, setMediaReady] = useState(0);
  const [bedUrl, setBedUrl] = useState<string | null>(null);
  // The narration in the rough play: each spoken line in its speaker's voice,
  // from the same sample route the inspector's Hear this line and the
  // Read-through use, fetched once per line and kept as an object URL. The
  // first tutorial rendered on 2026-10-18 came back "there is no voice
  // narration": the rough play ran the pictures and the bed and never spoke,
  // and nothing on screen said the finished video would. A speaker with no
  // voice on the brand is silent here and gets the default voice in the
  // render, and the readout says so.
  // Keyed by voice, direction and text, and holding the PROMISE rather than
  // the URL: the first press of play asked for line 1 again on every frame
  // for as long as its first sample was still being made — the play effect
  // was keyed on an array rebuilt each render, and nothing remembered a fetch
  // that had not yet answered. Several hundred billed calls in eight seconds.
  // A promise in the map is a line already asked for, answered or not.
  const narration = useRef<Map<string, Promise<string | null>>>(new Map());
  const lineAudio = useRef<HTMLAudioElement | null>(null);
  const playheadRef = useRef<number>(0);
  const startsRef = useRef<number[]>([]);
  const bed = useRef<HTMLAudioElement | null>(null);
  // A banner picked on the timeline, for editing where it is.
  const [selectedLayer, setSelectedLayer] = useState<string | null>(null);
  const [layers, setLayers] = useState<StoredLayer[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  // Bumped on every save and restore, so the history list follows the work
  // rather than showing what was there when the drawer opened.
  const [saves, setSaves] = useState(0);
  // Which tool the rail last pointed at. It scrolls the matching control into
  // view and marks it, rather than opening a second way to edit the same thing.
  const [tool, setTool] = useState<string | null>(null);
  // The card's brand, for the voice each speaker is read in. Fetched rather
  // than threaded through, because the card already knows its brand id and the
  // alternative is a prop every caller of this drawer has to supply.
  const [brand, setBrand] = useState<Brand | null>(null);
  // The library, for naming a proposed bed. The tray fetches its own list for
  // dragging; this one is the director's, and both are the same cheap call.
  const [assets, setAssets] = useState<Asset[]>([]);
  const [episodeAssets, setEpisodeAssets] = useState<Set<string>>(new Set());
  const [episodeSources, setEpisodeSources] = useState<Set<string>>(new Set());
  const [assembleAsk, setAssembleAsk] = useState(0);   // P8: the phase bar asks the director to assemble
  // The kind of asset being dragged from the tray, so the tracks can say yes
  // or no before the drop lands.
  const [dragKind, setDragKind] = useState<Asset['kind'] | null>(null);
  // How an inserted scene comes in. Remembered across inserts within a card,
  // because a creator dropping three sponsor spots wants the same join thrice.
  const [insertJoin, setInsertJoin] = useState('cut');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const cutVideo = useRef<HTMLVideoElement>(null);
  const [shortStart, setShortStart] = useState(0);
  const [moments, setMoments] = useState<Moment[]>([]);
  const [shortPick, setShortPick] = useState<ShortPick | null>(null);
  const [shortPicks, setShortPicks] = useState<ShortPick[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  async function removeCard() {
    if (!d) return;
    const published = d.publications.some((p) => p.status === 'published');
    const warning = `Delete “${d.card.title}”?\n\n`
      + 'This removes the card, its script and its renders. It cannot be undone.'
      + (published
        ? '\n\nAnything already published stays on the platform — deleting this does not take the video down.'
        : '');
    if (!window.confirm(warning)) return;
    setError(null);
    try {
      await api.deleteCard(id);
      onChanged(d.card.stage);   // refreshes the board
      onClose();
    } catch (e) { setError((e as Error).message); }
  }
  const [shortLen, setShortLen] = useState(45);
  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [speakers, setSpeakers] = useState('');
  // Seeded from the card's own cast once it loads, then from the show's cast
  // (Talent), until the person types in the box. It used to start as "Niki,
  // Dave" on every card in every workspace — two people nobody had cast,
  // which read as "every production has the same characters".
  const [speakersTyped, setSpeakersTyped] = useState(false);
  useEffect(() => {
    if (speakersTyped || d?.speakers?.length) return;
    const cast = brand?.voices ? Object.keys(brand.voices) : [];
    setSpeakers(cast.slice(0, 4).join(', '));
  }, [brand, d?.speakers, speakersTyped]);
  const [seconds, setSeconds] = useState(90);
  const [dirty, setDirty] = useState(false);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  // The collection's picture ids, joined: the pickers reload when it changes.
  const [pictureKey, setPictureKey] = useState('');
  // The recordings whose words are known, and the form for turning one into
  // scenes. Null while the form is closed.
  const transcribed = sources.filter((s) => s.transcript_status === 'ready');
  const [fromSource, setFromSource] = useState<{ sourceId: string; speaker: string; mode: 'replace' | 'append' } | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [when, setWhen] = useState('');
  const poll = useRef<number | null>(null);

  // The programme's clock, as the timeline draws it: measured seconds when a
  // render produced them, the estimate otherwise.
  const secs = d ? secondsPerLine(d.rundown.length, d.lineSeconds ?? []) : [];
  const starts = startOffsets(secs);
  const totalSeconds = secs.reduce((a, b) => a + b, 0);
  const joins = d ? d.rundown.slice(1).map((t) => ({
    kind: t.transition?.kind ?? d.card.mix?.transition?.kind ?? 'cut',
    seconds: t.transition?.seconds ?? d.card.mix?.transition?.seconds ?? 0.5,
  })) : [];
  const placed = d ? placeLayers(layers, sceneStarts(d.rundown.map((t) => t.id), secs)) : [];
  // A join at 0% is the scene the playhead has just reached, not the one
  // before it: clicking a block lands exactly on its start, and drawing the
  // previous scene there made the preview look one scene behind the inspector.
  const joinRaw = d && playhead !== null ? joinAt(starts, joins, playhead) : null;
  const join = joinRaw && joinRaw.progress > 0 ? joinRaw : null;
  const onScreen = playhead !== null ? layersAt(placed, playhead) : [];

  function seek(t: number) {
    setPlaying(false);
    const at = Math.min(Math.max(0, t), Math.max(0, totalSeconds));
    setPlayhead(at);
    setSelectedLine(sceneAt(starts, secs, at));
  }

  // The clock. One rAF loop that owns real time and nothing else: it steps the
  // playhead and stops at the end. Everything on screen is a function of the
  // playhead, so there is exactly one moving part.
  useEffect(() => {
    if (!playing) return;
    if (!(totalSeconds > 0)) { setPlaying(false); return; }
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setPlayhead((was) => {
        const t = tick(was ?? 0, dt, totalSeconds);
        if (t.done) setPlaying(false);
        return t.at;
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, totalSeconds]);

  // The scene under the playhead follows the clock. Kept out of the loop so
  // the loop stays about time: this is the one place the two meet.
  useEffect(() => {
    if (!playing || playhead === null) return;
    const at = sceneAt(starts, secs, playhead);
    setSelectedLine((was) => (was === at ? was : at));
  }, [playing, playhead, starts, secs]);

  function togglePlay() {
    if (!d?.rundown.length) return;
    setPlaying((was) => {
      if (was) return false;
      // Pressing play at the end starts again from the top, which is what
      // every transport in the world does.
      setPlayhead((at) => (at === null || at >= totalSeconds - 0.05 ? 0 : at));
      return true;
    });
  }

  // The footage for the scene being played, signed once and remembered. A
  // failure is quiet on purpose: the still the preview already draws stays on
  // screen, which is strictly better than an error over a working timeline.
  const liveTurn = playhead !== null && selectedLine !== null ? d?.rundown[selectedLine] ?? null : null;
  // Only footage runs. A still is the frame the preview already drew.
  const liveSourceId = playing ? footageOf(liveTurn, sources) : null;
  useEffect(() => {
    if (!liveSourceId || media.current.has(liveSourceId)) return;
    let live = true;
    api.sourceMedia(liveSourceId)
      .then((r) => { if (live && r.media?.url) { media.current.set(liveSourceId, r.media.url); setMediaReady((n) => n + 1); } })
      .catch(() => undefined);
    return () => { live = false; };
  }, [liveSourceId]);

  // The next scene's clip, fetched while this one plays. Signing a URL takes
  // long enough to be a visible gap at a cut if it is left until the cut.
  useEffect(() => {
    if (!playing || selectedLine === null || !d) return;
    const next = footageOf(d.rundown[selectedLine + 1], sources);
    if (!next || media.current.has(next)) return;
    let live = true;
    api.sourceMedia(next)
      .then((r) => { if (live && r.media?.url) { media.current.set(next, r.media.url); setMediaReady((n) => n + 1); } })
      .catch(() => undefined);
    return () => { live = false; };
  }, [playing, selectedLine, d]);

  // The bed, under the whole programme, exactly as the timeline draws it.
  // The bed is stored as a key, so it is matched back to the library row the
  // same way the timeline names it. No key, no bed, no sound.
  const bedKey = d?.card.mix?.music?.key ?? null;
  const bedAssetId = bedKey ? assets.find((a) => a.media_key === bedKey)?.id ?? null : null;
  useEffect(() => {
    if (!bedAssetId) { setBedUrl(null); return; }
    let live = true;
    api.assetUrl(bedAssetId).then((u) => { if (live) setBedUrl(u.url); }).catch(() => { if (live) setBedUrl(null); });
    return () => { live = false; };
  }, [bedAssetId]);

  useEffect(() => {
    const el = bed.current;
    if (!el || playhead === null) return;
    if (Math.abs(el.currentTime - playhead) > 0.4) { try { el.currentTime = playhead; } catch { /* not ready */ } }
    if (playing) void el.play().catch(() => undefined); else el.pause();
  }, [playing, playhead, bedUrl]);

  useEffect(() => { playheadRef.current = playhead ?? 0; }, [playhead]);
  useEffect(() => { startsRef.current = starts; });

  /** The sample URL for a line, asked for once. Null for a title, an empty line, or a speaker with no voice. */
  const lineSample = useCallback((i: number): Promise<string | null> => {
    const t = d?.rundown[i];
    if (!t || t.kind === 'title' || !t.text.trim()) return Promise.resolve(null);
    const voice = brand?.voices?.[t.speaker];
    if (!voice) return Promise.resolve(null);
    const key = `${voice}|${t.read?.trim() ?? ''}|${t.text}`;
    const asked = narration.current.get(key);
    if (asked) return asked;
    const p = api.voiceSample(voice, t.read?.trim() || undefined, t.text).catch(() => null);
    narration.current.set(key, p);
    return p;
  }, [d, brand]);

  // The line under the playhead is spoken, from where the playhead is in it,
  // and the next line is fetched while this one plays so a cut is not a gap.
  // Pausing pauses the voice with the picture; a seek while paused says nothing.
  useEffect(() => {
    if (!playing || selectedLine === null || !d) { lineAudio.current?.pause(); return; }
    let live = true;
    const i = selectedLine;
    void lineSample(i).then((url) => {
      if (!live) return;
      lineAudio.current?.pause();
      if (!url) return;
      const a = new Audio(url);
      lineAudio.current = a;
      const offset = sceneElapsed(startsRef.current, i, playheadRef.current);
      if (offset > 0.25) { try { a.currentTime = offset; } catch { /* not seekable yet */ } }
      void a.play().catch(() => undefined);
    });
    void lineSample(i + 1);
    return () => { live = false; };
    // Keyed on the line and the transport only. The start times are read from
    // a ref because the array is rebuilt every render, and an effect keyed on
    // it ran every frame.
  }, [playing, selectedLine, lineSample]);

  // Every sample is an object URL; they go with the drawer.
  useEffect(() => () => {
    lineAudio.current?.pause();
    for (const p of narration.current.values()) void p.then((url) => { if (url) URL.revokeObjectURL(url); });
    narration.current.clear();
  }, []);

  /** Who the rough play cannot voice: spoken lines whose speaker has no voice on the brand. */
  const unvoiced = d ? [...new Set(d.rundown.filter((t) => t.kind !== 'title' && t.text.trim() && !brand?.voices?.[t.speaker]).map((t) => t.speaker))] : [];

  // What the stage is showing right now, if it is moving.
  const motion = playing && liveTurn && liveSourceId && media.current.get(liveSourceId) && playhead !== null && selectedLine !== null
    ? {
        url: media.current.get(liveSourceId)!,
        at: footageTime(liveTurn, sceneElapsed(starts, selectedLine, playhead)),
        playing,
        muted: footageMuted(liveTurn),
      }
    : null;

  // ← → step the playhead a second (shift: five); Home and End go to the
  // ends. Only when nothing is being typed — a caption box keeps its arrows.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      if (!d || !d.rundown.length) return;
      const step = e.shiftKey ? 5 : 1;
      const from = playhead ?? (selectedLine !== null ? (starts[selectedLine] ?? 0) : 0);
      if (e.key === 'ArrowRight') { e.preventDefault(); seek(from + step); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); seek(from - step); }
      else if (e.key === 'Home') { e.preventDefault(); seek(0); }
      else if (e.key === 'End') { e.preventDefault(); seek(totalSeconds); }
      // Space plays and pauses, the way it does in every editor. Scrubbing by
      // hand stops the clock: two things moving the playhead at once is the
      // bug that makes a transport feel broken.
      else if (e.key === ' ') { e.preventDefault(); togglePlay(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, playhead, selectedLine, totalSeconds]);

  const readout = d && playhead !== null && selectedLine !== null
    ? [
        `${mmss(playhead)} / ${mmss(totalSeconds)}`,
        `scene ${selectedLine + 1} of ${d.rundown.length}`,
        join ? `${join.kind} into scene ${join.to + 1} · ${Math.round(join.progress * 100)}%` : null,
        onScreen.length ? `on screen: ${onScreen.map((l) => l.props?.text ?? '').join(' · ')}` : null,
        unvoiced.length ? `${unvoiced.join(', ')} ${unvoiced.length === 1 ? 'has' : 'have'} no voice on the brand: silent here, the default voice in the render` : null,
      ].filter(Boolean).join(' · ')
    : null;

  async function load() {
    try {
      const next = await api.card(id); setD(next); onChanged(next.card.stage as Stage);
      if (!speakersTyped && next.speakers?.length) setSpeakers(next.speakers.join(', '));
    }
    catch (e) { setError((e as Error).message); }
    // Separately, and never fatally: a card with no layers is the normal case,
    // and failing to list them should not stop the card opening.
    try { setLayers(await api.layers(id)); } catch { setLayers([]); }
    // Likewise: a card nobody has asked the director about is the normal case.
    try { setSuggestions(await api.suggestions(id)); } catch { setSuggestions([]); }
  }

  useEffect(() => {
    api.connectors().then((r) => setConnectors(r.connectors)).catch(() => setConnectors([]));
    api.assets().then(setAssets).catch(() => setAssets([]));
    // Only ready footage is offerable: a source still ingesting would render as
    // a panel, which looks like the cut was ignored rather than not ready.
    api.sources().then((r) => setSources(r.sources.filter((x) => x.status === 'ready')))
      .catch(() => setSources([]));
  }, []);

  // The brand, once the card says which it is. This used to run with the
  // mount effect above, before the card had loaded, so it looked for
  // `undefined` among the brands and kept none: every speaker then read as
  // "default voice", Hear this line was shut, and the rough play had nobody
  // to speak with — on a brand whose kit named a voice for every one of them.
  const brandId = d?.card.brand_id ?? null;
  useEffect(() => {
    if (!brandId) { setBrand(null); return; }
    let live = true;
    api.brands().then((bs) => { if (live) setBrand(bs.find((b) => b.id === brandId) ?? null); })
      .catch(() => { if (live) setBrand(null); });
    return () => { live = false; };
  }, [brandId]);

  // The thumbnail is what gets clicked, so it is shown rather than mentioned.
  useEffect(() => {
    // What would go wrong if this were published now.
    //
    // The catch used to be `setFindings([])`, and the list below only renders
    // when it is non-empty — so a preflight that FAILED was pixel-identical to
    // one that found nothing wrong, directly above the control that publishes
    // to a real channel. "We could not check" and "we checked and it is fine"
    // are opposite answers and looked the same.
    //
    // The dependency list was wrong too. It keyed on thumbnail_key while the
    // comment claimed it reloaded whenever the card changed, so the blockers
    // shown did not move after editing the rundown or the SEO pack. Stale
    // blockers above an irreversible action are worse than none: they are
    // believed.
    if (d) {
      api.preflight(id)
        .then((f) => { setFindings(f); setPreflightError(null); })
        .catch((e) => { setFindings([]); setPreflightError((e as Error).message); });
    }
    if (d?.job?.status === 'complete' && moments.length === 0) {
      api.shortCandidates(id).then((r) => { setMoments(r.moments); setShortPick(r.pick); setShortPicks(r.picks ?? []); }).catch(() => undefined);
    }
    if (d?.job?.thumbnail_key && !thumbUrl) {
      api.thumbnail(id).then((r) => setThumbUrl(r.download.url)).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    d?.job?.thumbnail_key,
    d?.job?.status,          // a render finishing changes every answer below
    d?.card?.seo_title,      // preflight checks the title length
    d?.card?.seo_description,
    d?.card?.stage,
  ]);

  useEffect(() => { void load(); return () => { if (poll.current) window.clearInterval(poll.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll only while a render is actually in flight. A timer that runs whatever
  // the state is turns an idle tab into a billed request every few seconds.
  useEffect(() => {
    const live = d?.job && (d.job.status === 'queued' || d.job.status === 'running');
    if (!live) { if (poll.current) { window.clearInterval(poll.current); poll.current = null; } return; }
    if (poll.current) return;
    poll.current = window.setInterval(() => void load(), 6000);
    return () => { if (poll.current) { window.clearInterval(poll.current); poll.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.job?.status]);

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(label); setError(null);
    try { await fn(); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  function editTurn(i: number, patch: Partial<Turn>) {
    if (!d) return;
    const rundown = d.rundown.map((t, n) => (n === i ? { ...t, ...patch } : t));
    setD({ ...d, rundown }); setDirty(true);
  }

  /**
   * Drop a scene into the timeline — a sponsor, a product shot, a title card.
   *
   * Its length is DECLARED, not discovered. Every other scene is as long as
   * its narration turns out to be once the renderer has synthesised it, which
   * means a creator cannot ask for eight seconds; eight seconds is not a
   * property of a sentence. This one is eight seconds because it says so.
   *
   * `beat: 0` and not the usual 0.35, so eight seconds means eight seconds.
   * A beat is how a READ breathes — it belongs after a line somebody speaks,
   * and appending one here would hand back a scene 0.35s longer than the
   * number in the field, which is the sort of small dishonesty that makes a
   * creator stop trusting the timeline.
   */
  function insertScene(after: number | null, join: string = 'cut', kind: 'line' | 'title' = 'line') {
    if (!d) return;
    const at = after === null ? d.rundown.length - 1 : after;
    const scene: Turn = {
      speaker: d.rundown[at]?.speaker ?? d.speakers[0] ?? 'Host',
      // A title scene opens the show: the video's title set large, a small
      // line under it, four seconds, no words spoken.
      ...(kind === 'title' ? { kind: 'title' as const, text: d.card.title, sub: 'coming up' } : { text: '' }),
      emotion: 'neutral', gesture: 'none', shot: 'wide',
      beat: 0, holdSeconds: kind === 'title' ? 4 : 8,
      // How it comes IN — the join before it, which a scene carries. A cut
      // stores nothing, because a cut is the absence of a transition and the
      // card's default already says so; storing 'cut' explicitly would show a
      // dot on the join for a choice that changed nothing.
      ...(join !== 'cut' ? { transition: { kind: join } } : {}),
    };
    const rundown = [...d.rundown];
    rundown.splice(at + 1, 0, scene);
    setD({ ...d, rundown }); setDirty(true);
    // Select it, because the next thing anyone does is choose its footage.
    setSelectedLine(at + 1);
  }

  /**
   * Footage dropped on a scene: that scene cuts to it.
   *
   * A scene cites a SOURCE, not an asset — the probed, normalised copy the
   * renderer can cut — so an asset that has no source yet gets one made. If a
   * source for this asset already exists it is reused, because two sources for
   * one clip is two names on the timeline for one thing. The cut is an edit to
   * the script like any other and waits for Save.
   */
  async function dropFootage(i: number, asset: DragAsset, asSecond = false, lane?: FootageLane) {
    if (!d) return;
    // The drop runs under run(), which reloads the card when it is done. An
    // edit kept only in local state was overwritten by that reload before the
    // block was ever drawn — every footage drop on the timeline was lost, and
    // nothing said so. So the patch is SAVED, like a bed or a cue, and the
    // reload brings it back.
    const commit = async (n: number, patch: Partial<Turn>) => {
      const rundown = d.rundown.map((t, k) => (k === n ? { ...t, ...patch } : t));
      await api.saveRundown(id, rundown);
      setDirty(false);
    };
    // A take from the tray IS a source already — probed, normalised, with its
    // words — so it is cited as it is. Minting a second source from it would
    // need an asset it does not have, and would be a second name for one clip.
    let source = asset.sourceId
      ? sources.find((s) => s.id === asset.sourceId)
      : sources.find((s) => s.asset_id === asset.id);
    if (asset.sourceId) {
      if (!source) {
        // Not in the ready set this card loaded — captured since, most likely.
        const all = await api.sources();
        source = all.sources.find((s) => s.id === asset.sourceId);
        if (!source) throw new Error('that take is gone — refresh the tray');
        setSources(all.sources.filter((x) => x.status === 'ready' || x.id === source!.id));
      }
      // The take joins the episode's collection (093), as it does from the Record screen.
      await api.collect(id, { sourceId: source.id, note: 'a take' }).catch(() => undefined);
    }
    if (!source) {
      source = await api.sourceFromAsset(asset.id, asset.name, asset.kind === 'image' ? 'file' : 'tape');
      // Refetched rather than appended, and unfiltered on purpose: a source
      // made from a video may still be normalising, and the block should say
      // "missing source" until it is ready rather than pretend it can render.
      const all = await api.sources();
      setSources(all.sources.filter((x) => x.status === 'ready' || x.id === source!.id));
    }
    // Alt-dropped on a scene that already has footage: the SECOND camera, in
    // the corner — the other angle of the same read. The first source keeps
    // the sound and the length. A still cannot be a second camera: it is a
    // picture over a picture, not another angle, and the renderer would hold
    // one frame in the corner for the scene.
    const current = d.rundown[i];
    if (asSecond && current?.sourceId && current.sourceId !== source.id && (source.duration_ms ?? 0) > 0) {
      await commit(i, { second: { layout: 'pip', ...(current.second ?? {}), sourceId: source.id } });
      setSelectedLine(i);
      return;
    }
    // The lane the clip landed on is the mode. A-roll speaks — the take's own
    // sound and length — so a clip the probe found silent is refused there
    // with the reason, rather than cut into a scene that would play nothing.
    // B-roll runs under the narration, muted, whatever the clip brought.
    if (lane === 'aroll') {
      if (source.has_audio === false) throw new Error(`${source.name} has no sound to speak — put it on B-roll`);
      await commit(i, { sourceId: source.id, audio: 'sync' });
    } else if (lane === 'broll') {
      await commit(i, { sourceId: source.id, audio: 'over' });
    } else {
      // Dropped on the scene block itself: a camera take brings its own words, so cut to it with its own sound.
      await commit(i, source.kind === 'camera' && source.has_audio ? { sourceId: source.id, audio: 'sync' } : { sourceId: source.id });
    }
    setSelectedLine(i);
  }

  /** Audio dropped on the Sound track: it becomes the bed. Saved at once, like the mix panel. */
  async function dropBed(asset: DragAsset) {
    if (!d) return;
    const card = await api.setMix(id, { assetId: asset.id });
    setD({ ...d, card });
  }

  // A sound cue (086) on the scene the drop landed on. It hangs from the
  // scene like a banner, so the scene has to be saved; plays from the scene's
  // start for as long as the file, up to ten seconds — a stinger, not a bed.
  async function dropCue(i: number, asset: DragAsset) {
    if (!d) return;
    const scene = d.rundown[i];
    if (!scene?.id) { setError('Save the script first — a cue hangs from a scene, and this one is not saved yet.'); return; }
    await api.addLayer(id, {
      track: 'sound', anchorId: scene.id, offsetSeconds: 0,
      seconds: Math.max(0.5, Math.min(asset.seconds ?? 3, 10)),
      props: { assetId: asset.id, gainDb: -6 },
    });
    setLayers(await api.layers(id));
    setSaves((n) => n + 1);
  }

  function moveTurn(from: number, to: number) {
    if (!d || from === to) return;
    const rundown = [...d.rundown];
    const [moved] = rundown.splice(from, 1);
    if (!moved) return;
    rundown.splice(to, 0, moved);
    setD({ ...d, rundown }); setDirty(true);
    // Follow the block that moved, so the line editor below stays on the line
    // the person just dragged rather than on whatever slid into its place.
    setSelectedLine(to);
  }

  /**
   * A banner on a scene.
   *
   * Anchored to the scene's ID, which is why it refuses before the script has
   * been saved: an unsaved line has no id yet, and the API would rightly
   * answer "no such scene on this card". Saying so here is better than sending
   * a request that cannot succeed.
   */
  // The show's mark, from the brand's defaults, on the first scene for as
  // long as the programme runs. Once per card: a second press is a no-op.
  async function addMark(assetId?: string) {
    if (!d) return;
    // E2: one of the show's logos when named; else the default mark.
    const logo = assetId ? { assetId, position: brand?.card_defaults?.logo?.position, height: brand?.card_defaults?.logo?.height } : brand?.card_defaults?.logo;
    const first = d.rundown[0];
    if (!logo) { setError("Set the show's mark in the Brand kit first — under How a new card starts, or tick a logo under Cast."); return; }
    if (!first?.id) { setError('Save the script first — the mark hangs from the first scene.'); return; }
    if (layers.some((l) => l.track === 'image' && l.props?.assetId === logo.assetId)) return;
    await run('layer', async () => {
      await api.addLayer(id, {
        track: 'image', anchorId: first.id!, offsetSeconds: 0, seconds: 3600,
        props: { assetId: logo.assetId, position: logo.position ?? 'bottom-left', height: logo.height ?? 120 },
      });
      setLayers(await api.layers(id));
    });
  }

  /**
   * D2: dress the episode in the house style, in one move — the show open
   * after the cold open (if there is none), the mark for the whole programme,
   * the social bar and SUBSCRIBE at the end. Each piece is the same thing the
   * buttons do one at a time; what is already there is left alone.
   */
  async function dressEpisode() {
    if (!d) return;
    if (dirty) { setError('Save the script first — the dressing hangs from saved scenes.'); return; }
    let rundown = d.rundown;
    if (rundown.length >= 2 && !rundown.some((t) => t.kind === 'title')) {
      const open: Turn = {
        kind: 'title', speaker: rundown[0]!.speaker, text: d.card.title, sub: 'coming up',
        emotion: 'neutral', gesture: 'none', shot: 'wide', beat: 0, holdSeconds: 4,
      };
      rundown = [rundown[0]!, open, ...rundown.slice(1)];
      await run('save', async () => { await api.saveRundown(id, rundown); });
      await load();
    }
    // S5: the end card — the last seconds are where the next click lives.
    // A held title at the end, the thanks large and the ask beneath; the
    // SUBSCRIBE button and the social bar hang from it.
    if (rundown.length >= 3 && rundown[rundown.length - 1]?.kind !== 'title') {
      const tail = rundown[rundown.length - 1]!;
      const end: Turn = {
        kind: 'title', speaker: tail.speaker, text: 'Thanks for watching', sub: 'the next one is coming — subscribe',
        emotion: 'neutral', gesture: 'none', shot: 'wide', beat: 0, holdSeconds: 8,
      };
      rundown = [...rundown, end];
      await run('save', async () => { await api.saveRundown(id, rundown); });
      await load();
    }
    const last = Math.max(0, rundown.length - 1);
    if (brand?.card_defaults?.logo) await addMark();
    if (!layers.some((l) => l.props?.text === 'SUBSCRIBE')) await addTag('subscribe', last);
    if (!layers.some((l) => l.props?.text === 'Follow us')) {
      const b = (await api.brands()).find((x) => x.id === d.card.brand_id);
      if (socialLine(b?.handles)) await addTag('socials', last);
    }
  }

  // A tag — LIVE, Coming up, DEBATE — on the selected scene, from a preset.
  async function addTag(presetId: string, at: number | null = null) {
    if (!d) return;
    let preset = TAG_PRESETS.find((t) => t.id === presetId);
    if (presetId.startsWith('element:')) {
      // E1: one of the show's saved elements, placed as it was saved.
      const el = brand?.elements?.find((e) => e.id === presetId.slice('element:'.length));
      if (el) preset = { id: presetId, label: el.label, seconds: el.seconds ?? (el.props.style === 'bug' || el.props.style === 'button' ? 3600 : 6), props: el.props };
    }
    if (presetId === 'socials') {
      // F8: the social bar, from the brand's handles — a lower third reading
      // FOLLOW US with the handles on its second line.
      const brand = (await api.brands()).find((b) => b.id === d.card.brand_id);
      const line = socialLine(brand?.handles);
      if (!line) { setError('Add the show\'s handles in the Brand kit first — the bar is drawn from them.'); return; }
      preset = { id: 'socials', label: 'Follow us', seconds: 8, props: { text: 'Follow us', style: 'lower-third', position: 'bottom', size: 34, sub: line } };
    }
    const i = at ?? selectedLine ?? 0;
    const scene = d.rundown[i];
    if (!preset) return;
    if (presetId === 'date') {
      // F11: the scene's footage is dated when it was filed; a scene with no
      // footage is dated today.
      const src = scene?.sourceId ? sources.find((s) => s.id === scene.sourceId) : undefined;
      preset = { ...preset, props: { ...preset.props, text: dateStamp(src?.created_at ? new Date(src.created_at) : new Date()) } };
    }
    if (!scene?.id) { setError('Save the script first — a tag hangs from a scene.'); return; }
    await run('layer', async () => {
      await api.addLayer(id, {
        track: 'text', anchorId: scene.id!, offsetSeconds: 0,
        seconds: preset.seconds, props: preset.props,
      });
      setLayers(await api.layers(id));
    });
  }

  async function addLayer(i: number) {
    if (!d) return;
    const scene = d.rundown[i];
    if (!scene?.id) {
      setError('Save the script first — a banner hangs from a scene, and this one is not saved yet.');
      return;
    }
    const text = window.prompt('Banner text', d.speakers[0] ?? 'Presenter');
    if (!text?.trim()) return;
    await run('layer', async () => {
      await api.addLayer(id, {
        track: 'text',
        anchorId: scene.id!,
        offsetSeconds: 0.3,
        seconds: Math.max(1, Math.min(6, (d.lineSeconds?.[i] ?? 4) - 0.6)),
        props: { text: text.trim(), position: 'bottom' },
      });
      setLayers(await api.layers(id));
    });
  }

  function removeTurn(i: number) {
    if (!d) return;
    setD({ ...d, rundown: d.rundown.filter((_, n) => n !== i) }); setDirty(true);
  }

  /** The names in the Script box, or undefined when it is empty — the API takes a cast or none, never an empty one. */
  function castNames(): string[] | undefined {
    const cast = speakers.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 4);
    return cast.length ? cast : undefined;
  }

  function addTurn() {
    if (!d) return;
    const last = d.rundown[d.rundown.length - 1];
    const next: Turn = {
      speaker: d.speakers.find((s) => s !== last?.speaker) ?? last?.speaker ?? 'Host',
      // `wide`, not `single`. This said 'single' when nothing read it, so every
      // hand-added line was really a wide. Now that the renderer honours the
      // field, keeping 'single' would silently turn every new line into a
      // close-up — the default has to be the thing that used to happen.
      text: '', emotion: 'neutral', gesture: 'none', shot: 'wide', beat: 0.35,
    };
    setD({ ...d, rundown: [...d.rundown, next] }); setDirty(true);
  }

  if (!d) {
    return (
      <div className="drawer">
        <div className="drawer-hd"><b>Loading…</b><button className="btn" onClick={onClose}>Close</button></div>
        {error && <div className="err">{error}</div>}
      </div>
    );
  }

  const rendering = d.job?.status === 'queued' || d.job?.status === 'running';

  // Leaving drops unsaved rundown edits — the brief and the mix save themselves,
  // the script does not — so ask before an exit this easy to reach.
  const leave = () => { if (dirty && !window.confirm('You have unsaved script edits. Leave without saving?')) return; onClose(); };

  return (
    <div className="drawer">
      {/* P8: the phases. The stage is the director's plan — gather, write and
          assemble, film, edit, render and review, schedule, publish — and the
          bar says what to do now, with the one click for it. */}
      <PhaseBar
        stage={d.card.stage}
        dirty={dirty}
        rendering={rendering}
        hasLines={d.rundown.length > 0}
        onClose={leave}
        onMove={(stage) => void run('move', async () => { await api.moveCard(id, stage); onChanged(stage); await load(); })}
        onGather={() => document.querySelector('[data-tool="collection"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
        onWrite={() => document.querySelector('[data-tool="produce"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' })}
        onAssemble={() => setAssembleAsk((n) => n + 1)}
        onFilm={onFilm ? () => { const i = selectedLine ?? 0; const t = d.rundown[i]; if (t) onFilm({ cardId: id, sceneIndex: i, line: t.kind === 'title' ? '' : t.text, title: d.card.title }); } : undefined}
        onRender={() => void run('render', () => api.render(id, { again: d.job?.status === 'complete' }))}
        onSchedule={() => document.querySelector('[data-tool="schedule"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
        onRunOfShow={() => {
          // P10: the run of show, as a file the host holds — every scene, its
          // time, who, the framing, the line, what is on screen, what is left to film.
          const md = runOfShow({
            title: d.card.title, speakers: d.speakers ?? [], rundown: d.rundown, lineSeconds: d.lineSeconds,
            measured: Boolean(d.lineSecondsAreMeasured), layers,
            takeNames: Object.fromEntries(sources.map((s) => [s.id, s.name])),
          });
          const url = URL.createObjectURL(new Blob([md], { type: 'text/markdown' }));
          const a = document.createElement('a'); a.href = url; a.download = `${d.card.title.replace(/[^\w-]+/g, '-').slice(0, 60) || 'run-of-show'}.md`; a.click();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
        }}
      />
      <div className="drawer-hd">
        <div style={{ minWidth: 0 }}>
          <b title={d.card.title}>{d.card.title}</b>
          <div className="sub">
            <span className="chip">{d.card.stage}</span>
            <RenderState d={d} />
            {d.rundown.length > 0 && (
              <span className="chip">{d.rundown.length} turns · ~{mmss(d.estimateSeconds)}</span>
            )}
          </div>
        </div>
        <div className="row">
          {/* The two things a creator does most, where the design puts them.
              Preview frame is the cheap one — it draws in a third of a second
              and follows unsaved edits — so it sits beside Render as the thing
              to press FIRST. Render is disabled while there are unsaved edits,
              because a render of the last saved state after you have changed
              something is a video that looks ignored rather than stale. */}
          <button
            className="btn"
            disabled={d.rundown.length === 0}
            title="Draw the selected scene now, without rendering"
            onClick={() => {
              if (selectedLine === null) setSelectedLine(0);
              document.querySelector('.framepv')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }}
          >
            Preview frame
          </button>
          <button className="btn pri" disabled={Boolean(busy) || rendering || d.rundown.length === 0 || dirty}
                  title={dirty ? 'Save your edits first — a render is of the saved script' : 'Render the programme'}
                  onClick={() => void run('render', () => api.render(id, { again: d.job?.status === 'complete' }))}>
            {rendering ? 'Rendering…'
              : d.job?.status === 'failed' ? 'Retry render'
              : d.job?.status === 'complete' ? 'Render again' : 'Render'}
          </button>
          <button className="btn" onClick={leave}>Close</button>
        </div>
      </div>

      {error && <div className="err">{error}</div>}

      <div className="drawer-bd">
        {/* Two columns, each its own scroller.
            LEFT is the surface a creator handles: the frame, the lanes, the
            lines. RIGHT is what they SET rather than touch — the director's
            proposals, the render, the mix, where it goes.

            Real containers rather than grid placement on the sections
            themselves. Auto-placement put them in columns that happened to
            look right for this exact set of sections and would have moved the
            moment one was added or hidden — a layout correct by coincidence.
            Containers also give each column its own scroll, so reaching the
            mix never scrolls the timeline out from under the cursor. */}
        <ToolRail
          active={tool}
          hasScene={selectedLine !== null}
          onPick={(t: Tool) => {
            if (!t.target) return;
            setTool(t.id);
            // The rail is a map. Every target is a real element already on this
            // screen, so pointing at it is scrolling and marking — never a
            // second editor to keep in step with the first.
            const el = document.querySelector(`[data-tool="${t.target}"]`);
            el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            (el as HTMLElement | null)?.focus?.();
          }}
        />

        <div className="col-work">
        {/* ── The brief (D14): the Idea phase's own artifact ── */}
        <section data-tool="brief">
          <h4>Brief</h4>
          <p className="hint">The angle in a paragraph and the three points the piece must land. The director writes it from the theme and the subject; the script follows it.</p>
          <BriefBox cardId={id} brief={d.card.brief ?? null} busy={Boolean(busy)} onChanged={(card) => setD({ ...d, card })} onError={setError} />
        </section>
        {/* ── Write ── */}
        <section>
          <h4>Script <ReadThrough rundown={d.rundown} voices={brand?.voices ?? null} /></h4>
          <div className="row wrap">
            <input value={speakers} onChange={(e) => { setSpeakersTyped(true); setSpeakers(e.target.value); }}
                   placeholder="Who speaks — Host, Guest; cast them under Talent for fixed voices" style={{ flex: '1 1 200px' }} />
            <label className="inline">
              Length
              <select value={seconds} onChange={(e) => setSeconds(Number(e.target.value))}>
                <option value={45}>45s</option><option value={90}>90s</option>
                <option value={180}>3 min</option><option value={600}>10 min</option>
                <option value={900}>15 min</option><option value={1800}>30 min</option>
              </select>
            </label>
            <button className="btn" disabled={Boolean(busy)}
                    onClick={() => void run('script', () =>
                      api.writeScript(id, castNames(), seconds))}>
              {busy === 'script' ? 'Writing…' : d.rundown.length ? 'Rewrite' : 'Write script'}
            </button>
            {/* The headline-only way in: the assistant writes the script AND
                arranges the piece, and you review a finished draft. Primary on
                an empty card, because it is the whole point of giving a card a
                headline; once a script exists, Rewrite is the safer verb. */}
            {d.rundown.length === 0 && (
              <button className="btn pri" disabled={Boolean(busy)}
                      data-tool="produce"
                      title="Write the script and arrange it — captions, cuts and a bed arrive as proposals to approve"
                      onClick={() => void run('produce', async () => {
                        const cast = castNames();
                        const r = await api.produce(id, { ...(cast ? { speakers: cast } : {}), targetSeconds: seconds });
                        setSuggestions(r.suggestions);
                        if (r.detail) setError(r.detail);
                      })}>
                {busy === 'produce' ? 'Producing…' : 'Produce from the headline'}
              </button>
            )}
          </div>
        </section>

        {/* ── Scenes from a recording ──
            The other way a rundown begins: not written, but heard. A source
            that has been transcribed (under Sources) becomes scenes, each a
            stretch of the recording played with its own sound, its words as
            its captions. One speaker for the whole import, because the
            service does not say who is talking; the guest's lines are
            renamed on the timeline afterwards. */}
        {transcribed.length > 0 && (
          <section>
            <div className="row between">
              <h4>Scenes from a recording</h4>
              {!fromSource && (
                <button className="btn" onClick={() => setFromSource({
                  sourceId: transcribed[0]!.id,
                  speaker: d.speakers?.[0] ?? (brand?.voices ? Object.keys(brand.voices)[0] : undefined) ?? 'Host',
                  mode: d.rundown.length ? 'append' : 'replace',
                })}>
                  Make scenes…
                </button>
              )}
            </div>
            {fromSource && (
              <div className="row wrap" style={{ marginTop: 8 }}>
                <select value={fromSource.sourceId} title="Which transcribed recording"
                        onChange={(e) => setFromSource({ ...fromSource, sourceId: e.target.value })}>
                  {transcribed.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input value={fromSource.speaker} maxLength={60} style={{ width: 140 }}
                       title="Who is talking — one name for every scene; rename the guest's lines afterwards"
                       onChange={(e) => setFromSource({ ...fromSource, speaker: e.target.value })} />
                <select value={fromSource.mode} title="Replace the rundown, or add the scenes after its last line"
                        onChange={(e) => setFromSource({ ...fromSource, mode: e.target.value as 'replace' | 'append' })}>
                  <option value="replace">replace the rundown</option>
                  <option value="append">add after the last scene</option>
                </select>
                <button className="btn pri" disabled={Boolean(busy) || !fromSource.speaker.trim()}
                        onClick={() => void run('scenes', async () => {
                          const r = await api.scenesFromSource(id, {
                            sourceId: fromSource.sourceId, speaker: fromSource.speaker.trim(), mode: fromSource.mode,
                          });
                          setFromSource(null); setDirty(false); setSaves((n) => n + 1);
                          setError(`${r.scenes} scenes made — each plays its own sound; the words are its captions.`);
                        })}>
                  {busy === 'scenes' ? 'Making…' : 'Make scenes'}
                </button>
                <button className="btn" onClick={() => setFromSource(null)}>Cancel</button>
              </div>
            )}
          </section>
        )}

        {/* ── An empty card ──
            The timeline, the library tray and the Inspector only exist once
            there is a scene for them to be about, so a new card showed a
            brief and a script box and nothing else — and a producer with a
            recording in hand had no idea where it went. This names the three
            doors and opens the plain one. */}
        {d.rundown.length === 0 && (
          <section className="pane-edit" data-tool="begin">
            <h4>No scenes yet</h4>
            <p className="hint">
              A card is one production: its own script, scenes, cast and media. Its scenes come from one of three places —
              <b> Record</b> a take on camera (it lands in Sources; “add to the video as a scene” puts it here),
              <b> Sources</b> › upload a recording › Transcribe › <b>Scenes from a recording</b> below,
              or <b>Write script</b> / <b>Produce from the headline</b> above and let the cast read it.
              Or start by hand: a blank scene opens the timeline, and footage from the library drops onto it.
            </p>
            <div className="row">
              <button className="btn pri" onClick={addTurn} title="A blank scene — the timeline, the lanes and the library tray appear, and footage drops onto it">
                Start with a blank scene
              </button>
            </div>
          </section>
        )}

        {/* ── Rundown ── */}
        {d.rundown.length > 0 && (
          <section className="pane-edit">
            <div className="row between">
              <h4>Rundown</h4>
              <div className="row">
                <button className="btn" onClick={addTurn}>Add turn</button>
                {/* A scene rather than a line: it holds for a length the
                    creator sets, and everything after it moves down by that
                    length — including every layer, because layers anchor to a
                    scene and not to a clock. That ripple is the reason a
                    sponsor is a scene and not an overlay: the viewer's own
                    scrub bar shows it as part of the video. */}
                <span className="insert-ctl">
                  <button className="btn" onClick={() => insertScene(selectedLine, insertJoin)}
                          title="Insert a held scene after the selected line — a sponsor, a product shot">
                    Insert scene
                  </button>
                  <button className="btn" onClick={() => insertScene(selectedLine, insertJoin, 'title')}
                          title="Insert the show open after the selected line — the title set large on the set, four seconds">
                    + title
                  </button>
                  {onFilm && (
                    <button className="btn" disabled={dirty}
                            title={dirty ? 'Save the script first' : 'Film the selected line on camera: the Record screen opens with this line on the prompter, and the take lands in this scene with its own sound'}
                            onClick={() => {
                              const i = selectedLine ?? 0;
                              const t = d.rundown[i];
                              if (!t) return;
                              onFilm({ cardId: id, sceneIndex: i, line: t.kind === 'title' ? '' : t.text, title: d.card.title });
                            }}>
                      Film this line
                    </button>
                  )}
                  {/* How the inserted scene comes in. From the brand's shelf,
                      like every other join; "cut in" is the default because it
                      stores nothing and is what a sponsor spot usually wants. */}
                  <select value={insertJoin} title="How the inserted scene comes in"
                          onChange={(e) => setInsertJoin(e.target.value)}>
                    {(d.shelfTransitions ?? [...LEGACY_TRANSITIONS]).map((k) => (
                      <option key={k} value={k}>{k === 'cut' ? 'cut in' : `${transitionLabel(k).toLowerCase()} in`}</option>
                    ))}
                  </select>
                </span>
                <button className="btn pri" disabled={!dirty || Boolean(busy)}
                        onClick={() => void run('save', async () => {
                          await api.saveRundown(id, d.rundown); setDirty(false);
                          setSaves((n) => n + 1);
                        })}>
                  {busy === 'save' ? 'Saving…' : dirty ? 'Save edits' : 'Saved'}
                </button>
              </div>
            </div>
            {/* What the selected line will look like, before paying for a
                render. It follows the UNSAVED script — the whole point is to
                see a shot change as it is made, not after a save. */}
            <FramePreview
              cardId={id}
              // Inside a join the picture is the scene being LEFT with the
              // next one arriving over it; the inspector stays on the scene
              // the playhead has reached.
              line={join ? join.from : selectedLine}
              rundown={d.rundown}
              title={d.card.title}
              into={join ? { line: join.to, alpha: join.progress } : null}
              readout={readout}
              motion={motion}
              caption={motion ? (liveTurn?.text?.trim() || null) : null}
              overlay={selectedLine !== null && (() => {
                // Only this scene's. A layer is anchored to a scene, so the
                // ones belonging to other scenes are not in this frame — and
                // drawing them here would be showing a shot that never exists.
                // With a playhead, only the ones on screen at that moment.
                const anchor = d.rundown[selectedLine]?.id;
                if (!anchor) return null;
                const onScene = layers.filter((l) => l.anchor_id === anchor);
                const shown = playhead === null ? onScene
                  : onScene.filter((l) => onScreen.some((p) => p.id === l.id));
                return (
                  <CanvasLayers
                    rig={brand?.card_defaults?.rig?.wordmark ? (brand.wordmark || brand.name) : null}
                    layers={shown}
                    suggestions={suggestions.filter(
                      (s) => s.status === 'pending' && s.kind === 'layer'
                        && s.payload.anchorId === anchor)}
                    busy={busy === 'decide'}
                    onDecide={(sid, decision) => void run('decide', async () => {
                      await api.decideSuggestion(id, sid, decision);
                      setSuggestions(await api.suggestions(id));
                      setLayers(await api.layers(id));
                    })}
                  />
                );
              })()}
            />
            {/* The programme as time, above the lines that make it. Selecting a
                block scrolls the matching line into view; dragging one reorders
                the script, which is the only edit a single-track timeline can
                honestly offer. */}
            {/* The bed, under the whole programme. One element for the card,
                pinned to the playhead like the footage is. */}
            {bedUrl && <audio ref={bed} src={bedUrl} preload="auto" style={{ display: 'none' }} />}
            <div data-tool="layers">
            <Timeline
              playing={playing}
              onTogglePlay={togglePlay}
              rundown={d.rundown}
              lineSeconds={d.lineSeconds ?? []}
              measured={d.lineSecondsAreMeasured}
              sources={sources}
              shelf={d.shelfTransitions ?? [...LEGACY_TRANSITIONS]}
              music={d.card.mix?.music ?? null}
              musicName={assets.find((a) => a.media_key && a.media_key === d.card.mix?.music?.key)?.name ?? null}
              dragKind={dragKind}
              onDropFootage={(i, asset, asSecond, lane) => void run('drop', () => dropFootage(i, asset, asSecond, lane))}
              onDropBed={(asset) => void run('drop', () => dropBed(asset))}
              onClearFootage={(i) => {
                // Back to the panel. The second camera rides on the first and
                // goes with it; a take's own sound ('sync') is footage sound,
                // so the scene reads over the panel again.
                const t = d.rundown[i];
                editTurn(i, { sourceId: null, second: null, ...(t?.audio === 'sync' ? { audio: 'over' } : {}) });
              }}
              onClearBed={() => void run('mix', async () => { await api.setMix(id, null); })}
              onDropCue={(i, asset) => void run('drop', () => dropCue(i, asset))}
              cueNames={Object.fromEntries(assets.map((a) => [a.id, a.name?.trim() || (a.media_key ?? a.storage_key).split('/').pop() || 'cue']))}
              transition={d.card.mix?.transition ?? null}
              fadeOut={d.card.mix?.fadeOut ?? null}
              onFadeOut={(s) => void run('mix', async () => { await api.setFadeOut(id, s); })}
              selected={selectedLine}
              onSelect={(i) => {
                // The frame is what a click on a scene is for. It used to also
                // scroll the page to that line's text, which took the frame
                // out of view the moment it was asked for.
                setSelectedLine(i);
                setPlayhead(starts[i] ?? 0);
                setSelectedLayer(null);
              }}
              playhead={playhead}
              onSeek={seek}
              onResizeLayer={(lid, seconds) => void run('layer', async () => {
                await api.updateLayer(id, lid, { seconds });
                setLayers(await api.layers(id));
                setSaves((n) => n + 1);
              })}
              onHold={(i, seconds) => editTurn(i, { holdSeconds: seconds })}
              selectedLayer={selectedLayer}
              onSelectLayer={(lid) => {
                setSelectedLayer(lid);
                const l = placed.find((x) => x.id === lid);
                if (l) seek(l.start);
              }}
              onReorder={moveTurn}
              onJoin={(i, kind) => editTurn(i, { transition: kind ? { kind } : null })}
              layers={layers}
              onAddLayer={(i) => void addLayer(i)}
              onAddMark={brand?.card_defaults?.logo || (brand?.logos ?? []).length ? (assetId) => void addMark(assetId) : undefined}
              logos={(brand?.logos ?? []).map((lid) => ({ id: lid, name: assets.find((a) => a.id === lid)?.name?.trim() || 'logo' }))}
              onAddTag={(preset) => void addTag(preset)}
              elements={brand?.elements ?? []}
              onRemoveLayer={(layerId) => void run('layer', async () => {
                await api.removeLayer(id, layerId);
                setLayers(await api.layers(id));
              })}
            />
            </div>

            {/* The library, where the tracks are. Drag a clip onto a scene or
                audio onto Sound; the track says whether it will take it. */}
            {/* This episode's collection (093): gathered first, the library
                behind it, so a card fetches what it uses and not everything. */}
            <Collection cardId={id} brandId={d.card.brand_id}
                        onChanged={(a, srcs) => { setEpisodeAssets(new Set(a)); setEpisodeSources(new Set(srcs)); setPictureKey([...a].sort().join(',')); }} />
            <AssetTray brandId={d.card.brand_id} onDragState={setDragKind} episode={episodeAssets} episodeSources={episodeSources} />

            <div className="turns">
              {d.rundown.map((t, i) => (
                <div className={`turn${selectedLine === i ? ' is-selected' : ''}`} id={`turn-${i}`} key={i}>
                  <div className="turn-hd">
                    <input className="speakerfield" value={t.speaker}
                           onChange={(e) => editTurn(i, { speaker: e.target.value })} />
                    <select value={t.emotion} onChange={(e) => editTurn(i, { emotion: e.target.value as Turn['emotion'] })}>
                      {EMOTIONS.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                    <select value={t.gesture} onChange={(e) => editTurn(i, { gesture: e.target.value as Turn['gesture'] })}>
                      {GESTURES.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                    {/* Framing. Honoured by the renderer since panels_for: a
                        single is one panel however big the cast is, a two-shot
                        is the speaker and one other, wide is everyone. It had
                        no control at all before, so the only non-default values
                        in the product came from the script generator. */}
                    <select value={t.shot ?? 'wide'} title="How this line is framed"
                            {...(selectedLine === i ? { 'data-tool': 'shotRow' } : {})}
                            onChange={(e) => editTurn(i, { shot: e.target.value as ShotKind })}>
                      {SHOTS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                    </select>
                    <select value={t.sourceId ?? ''} title="Cut to footage for this line"
                            onChange={(e) => { const src = sources.find((x) => x.id === e.target.value); editTurn(i, src?.kind === 'camera' && src.has_audio ? { sourceId: src.id, audio: 'sync' } : { sourceId: e.target.value || null }); }}>
                      <option value="">panel</option>
                      {episodeSources.size > 0 && (
                        <optgroup label="This episode">
                          {sources.filter((x) => episodeSources.has(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                        </optgroup>
                      )}
                      <optgroup label={episodeSources.size > 0 ? 'The library' : 'Sources'}>
                        {sources.filter((x) => !episodeSources.has(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </optgroup>
                    </select>
                    {t.sourceId && (
                      <>
                        <input className="secs" type="number" min={0} step={0.5}
                               {...(selectedLine === i ? { 'data-tool': 'sourceIn' } : {})}
                               value={t.sourceIn ?? 0} title="Seconds into the source — the in point"
                               onChange={(e) => editTurn(i, { sourceIn: Number(e.target.value) })} />
                        {/* The out point. It has existed in the schema since the
                            beginning, was shipped to the render plane, and was
                            read by nothing — so "in and out" really meant "in,
                            and a length you do not control". The renderer
                            honours it now: material past it is never shown, and
                            a line that outlasts the material holds the last
                            frame rather than ending the segment early. Blank
                            means "play on", which is what every line does
                            today. */}
                        <input className="secs" type="number" min={0} step={0.5}
                               {...(selectedLine === i ? { 'data-tool': 'sourceOut' } : {})}
                               value={t.sourceOut ?? ''} placeholder="out"
                               title="Stop at this many seconds into the source. Blank plays on."
                               onChange={(e) => editTurn(i, {
                                 sourceOut: e.target.value === '' ? undefined : Number(e.target.value),
                               })} />
                        {/* Whose sound. Three answers, and each one renders:
                            the voice reading the line over a silent shot; the
                            recording's own sound with nothing read aloud (the
                            scene then runs from the in point to the out point
                            and the line is its captions); or the voice over
                            the recording, ducked while the voice is talking.
                            "sync sound" was offered for a long time and
                            thrown away at the final mux — the finished file
                            carried the voice whatever was chosen here. */}
                        <select value={t.audio ?? 'over'}
                                title="Whose sound: the voice, the recording, or the voice over the recording"
                                onChange={(e) => editTurn(i, { audio: e.target.value as Turn['audio'] })}>
                          <option value="over">narrate over</option>
                          <option value="sync">sync sound</option>
                          <option value="mixed">narrate over its sound</option>
                        </select>
                        {/* Speed. The picture, and on a sync-sound scene the
                            recording with it. A narrated hold is still exactly
                            as long as its line; speed changes how much TAPE
                            that hold consumes, which the title says because it
                            is the thing that surprises people. */}
                        <select value={t.speed ?? ''} title="How fast the picture plays — and its sound, on a sync-sound scene"
                                {...(selectedLine === i ? { 'data-tool': 'speed' } : {})}
                                onChange={(e) => editTurn(i, {
                                  speed: e.target.value === '' ? null : Number(e.target.value),
                                })}>
                          <option value="">1× speed</option>
                          {SPEEDS.map((v) => (
                            <option key={v} value={v}>{v}× {v < 1 ? 'slow' : 'fast'}</option>
                          ))}
                        </select>
                        {/* Crop, as one of a few framings rather than a free
                            rectangle. A draggable box over a frame preview is
                            the right control and is a bigger piece of work; a
                            select that produces real, checked rectangles is
                            worth more today than a control that does nothing. */}
                        <select
                          value={cropName(t.crop)}
                          {...(selectedLine === i ? { 'data-tool': 'crop' } : {})}
                          title="Which part of the source to show"
                                                  onChange={(e) => {
                            // Choosing "custom" is not a change — it is the
                            // label for what is already set. Writing null on it
                            // would clear the crop the moment the select was
                            // touched and released.
                            if (e.target.value === 'custom') return;
                            editTurn(i, { crop: CROPS[e.target.value] ?? null });
                          }}
                        >
                          {Object.keys(CROPS).map((k) => (
                            <option key={k} value={k}>{k}</option>
                          ))}
                          {cropName(t.crop) === 'custom' && (
                            // A rectangle set by something other than this list
                            // must still show as what it is. A select whose
                            // value is not among its options renders BLANK,
                            // which reads as "no crop" over a shot that is
                            // cropped — the same trap as an off-shelf
                            // transition in the mix panel.
                            <option value="custom">custom</option>
                          )}
                        </select>
                        {/*
                          Movement, for a still. A clip already moves and the
                          render plane drops motion on one, so the control is
                          disabled there rather than offered and then ignored —
                          a control that does nothing is worse than no control.

                          A source made from an image carries no duration, which
                          is how a still is told apart here. When that is not yet
                          known the control stays enabled: hiding it on a still
                          would be the more annoying mistake.
                        */}
                        {(() => {
                          const src = sources.find((x) => x.id === t.sourceId);
                          const footage = typeof src?.duration_ms === 'number' && src.duration_ms > 0;
                          return (
                            <select
                              value={t.motion?.kind ?? 'none'}
                              {...(selectedLine === i ? { 'data-tool': 'motion' } : {})}
                              disabled={footage}
                              title={footage
                                ? 'This is footage — it already moves'
                                : 'Movement on a still'}
                              onChange={(e) => editTurn(i, {
                                motion: e.target.value === 'none'
                                  ? null
                                  : { kind: e.target.value as MotionKind },
                              })}
                            >
                              {MOTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                            </select>
                          );
                        })()}
                      </>
                    )}
                    <span className="spacer" />
                    <button className="btn tiny" onClick={() => removeTurn(i)} aria-label="Remove turn">×</button>
                  </div>
                  {t.holdSeconds !== undefined && !t.text.trim() ? (
                    // An inserted scene has no line to write. What it has is a
                    // length, and offering an empty script box here would ask
                    // for the one thing this scene is defined by NOT having.
                    <div className="turn-hold">
                      <label>
                        Holds for
                        <input className="secs" type="number" min={0.5} max={14400} step={0.5}
                               value={t.holdSeconds}
                               onChange={(e) => editTurn(i, {
                                 // Blank is not zero. `Number('')` is 0, and a
                                 // zero-second scene is one ffmpeg drops with
                                 // no error — a shot silently missing from the
                                 // cut. An empty field holds the last length.
                                 holdSeconds: e.target.value === ''
                                   ? t.holdSeconds
                                   : Number(e.target.value),
                               })} />
                        seconds
                      </label>
                      <span className="hint">
                        Everything after this moves down by that much.
                      </span>
                    </div>
                  ) : (<>
                    <textarea rows={2} value={t.text} onChange={(e) => editTurn(i, { text: e.target.value })}
                              placeholder={t.sourceId && t.audio === 'sync'
                                ? 'What is said in this stretch — the captions use it; nothing is read aloud'
                                : undefined}
                              title={t.sourceId && t.audio === 'sync'
                                ? `Sync sound: this scene plays the recording from ${t.sourceIn ?? 0}s${
                                    t.sourceOut !== undefined ? ` to ${t.sourceOut}s` : ' to the end'}. The words are its captions.`
                                : undefined} />
                    {t.text.includes('**') && (
                      <div className="sub-dim" style={{ fontSize: 12, marginTop: 2 }} title="The key word, as the captions will set it in the show's colour">
                        {t.text.split(/(\*\*[^*]+\*\*)/g).map((part, k) => part.startsWith('**') && part.endsWith('**')
                          ? <b key={k} style={{ color: 'var(--accent)' }}>{part.slice(2, -2)}</b>
                          : <span key={k}>{part}</span>)}
                      </div>
                    )}
                  </>)}
                </div>
              ))}
            </div>
          </section>
        )}

        </div>

        <div className="col-set">
        {/* Adjustments for whatever is selected, above the card's own panels.
            What you are holding, then what the card is. */}
        <section data-tool="shot">
          {/* Everything that exists at THIS moment, editable: the join the
              playhead is inside, every banner on screen, then the scene. Move
              the playhead and the column changes to what is there — the frame
              above and the settings here describe the same instant. A banner
              picked by hand on the lane is shown on its own. */}
          {join && (
            <div className="lyi" data-tool="join">
              <div className="row between">
                <span className="lbl-mono">Join · into scene {join.to + 1} · {Math.round(join.progress * 100)}%</span>
              </div>
              <div className="lyi-row">
                <label className="field">
                  Kind
                  <select value={d.rundown[join.to]?.transition?.kind ?? ''}
                          onChange={(e) => editTurn(join.to, { transition: e.target.value
                            ? { kind: e.target.value, seconds: d.rundown[join.to]?.transition?.seconds ?? join.seconds }
                            : null })}>
                    <option value="">card default ({d.card.mix?.transition?.kind ?? 'cut'})</option>
                    {(d.shelfTransitions ?? [...LEGACY_TRANSITIONS]).map((k) => <option key={k} value={k}>{k}</option>)}
                    <option value="cut">cut</option>
                  </select>
                </label>
                <label className="field">
                  Seconds
                  <input type="number" min={0.1} max={3} step={0.1}
                         value={d.rundown[join.to]?.transition?.seconds ?? join.seconds}
                         onChange={(e) => editTurn(join.to, { transition: { kind: d.rundown[join.to]?.transition?.kind ?? join.kind, seconds: Math.max(0.1, Math.min(3, Number(e.target.value) || 0.5)) } })} />
                </label>
                <span />
              </div>
            </div>
          )}
          {(() => {
            const chosen = selectedLayer ? layers.filter((x) => x.id === selectedLayer) : [];
            const here = playhead !== null && !selectedLayer
              ? layers.filter((x) => onScreen.some((o) => o.id === x.id)) : [];
            return [...chosen, ...here].map((l) => {
              const at = d.rundown.findIndex((t) => t.id === l.anchor_id);
              return (
                <LayerInspector
                  key={l.id}
                  layer={l}
                  sceneIndex={at >= 0 ? at : null}
                  cardId={d.card.id}
                  logos={brand?.logos}
                  pictureKey={pictureKey}
                  busy={busy === 'layer'}
                  onClose={() => setSelectedLayer(null)}
                  onSave={(patch) => void run('layer', async () => {
                    await api.updateLayer(id, l.id, patch);
                    setLayers(await api.layers(id));
                    setSaves((n) => n + 1);
                  })}
                  onRemove={() => void run('layer', async () => {
                    await api.removeLayer(id, l.id);
                    setLayers(await api.layers(id));
                    setSelectedLayer(null);
                  })}
                  onSaveElement={brand ? (label, props, seconds) => void run('layer', async () => {
                    const r = await api.addBrandElement(brand.id, { label, props, seconds });
                    setBrand({ ...brand, elements: r.elements });
                    setError(null);
                  }) : undefined}
                />
              );
            });
          })()}
          <Inspector
            turn={selectedLine !== null ? d.rundown[selectedLine] ?? null : null}
            index={selectedLine}
            total={d.rundown.length}
            seconds={(d.lineSeconds ?? [])[selectedLine ?? 0] ?? 0}
            measured={Boolean(d.lineSecondsAreMeasured)}
            speakers={d.speakers ?? []}
            brand={brand ?? null}
            cardId={d.card.id}
            pictureKey={pictureKey}
            sources={sources}
            backdropDim={d.card.mix?.background ? (d.card.mix.background.dim ?? 0) : null}
            onEdit={(patch) => { if (selectedLine !== null) editTurn(selectedLine, patch); }}
          />
        </section>
        {/* ── The assistant director ── */}
        {d.rundown.length > 0 && (
          <section data-tool="director">
            {/* 190: where the director may draw from. The fence the creator
                sees: the library, only what they put in this episode's
                collection, or nothing of the show's. */}
            <label className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span className="sub-dim">The director draws</span>
              <select value={d.card.sourcing ?? 'library'} disabled={Boolean(busy)}
                      onChange={(e) => void run('sourcing', async () => { await api.setSourcing(id, e.target.value as Sourcing); await load(); })}>
                {(Object.keys(SOURCING_LABEL) as Sourcing[]).map((k) => <option key={k} value={k}>{SOURCING_LABEL[k]}</option>)}
              </select>
            </label>
            <Director
              sourcing={d.card.sourcing ?? 'library'}
              assembleAsk={assembleAsk}
              onDress={() => void dressEpisode()}
              episodeGathered={episodeAssets.size + episodeSources.size > 0}
              onAssembled={async () => { if (d.card.stage === 'idea' || d.card.stage === 'scripted' || d.card.stage === 'filming') { await api.moveCard(id, 'editing'); onChanged('editing'); } await load(); }}
              cardId={id}
              rundown={d.rundown}
              lineSeconds={d.lineSeconds ?? []}
              suggestions={suggestions}
              sources={sources}
              assets={assets}
              brandId={d.card.brand_id}
              onChanged={async () => {
                // Approving makes a LAYER, so both lists move. Reloading both
                // beats guessing which one changed — an approval that leaves
                // the timeline showing the old lane is a banner the creator
                // has to refresh to believe in.
                setSuggestions(await api.suggestions(id));
                setLayers(await api.layers(id));
              }}
            />
          </section>
        )}

        {/* ── History ── */}
        <section data-tool="history">
          <h4>History</h4>
          <History
            cardId={id}
            reloadKey={saves}
            onRestored={async () => {
              // Everything on the card may have moved: script, mix, layers.
              // Reloading the whole thing beats guessing which — a restore
              // that leaves the timeline showing the old script is a rollback
              // the creator cannot believe in.
              await load();
              setLayers(await api.layers(id));
              setDirty(false);
              setSaves((n) => n + 1);
            }}
          />
        </section>

        {/* ── Render ── */}
        <section>
          <div className="row between">
            <h4>Render</h4>
            <div className="row">
              {rendering && (
                <button className="btn" onClick={() => void run('cancel', () => api.cancelRender(id))}>
                  Cancel
                </button>
              )}
              {/* Render itself lives in the header now. Two buttons for one
                  action is two places to get the disabled logic wrong. */}

            {/* The finished video, directly under the button that made it.
                This used to render with no heading AFTER the Shorts section,
                so somebody who pressed Render and looked at the Render section
                saw a thumbnail and concluded no video had been produced. It
                had — the worker logged the mp4 — it was just nowhere a person
                would look for it. */}
            {d.job?.status === 'complete' && (
              <div style={{ marginTop: 12 }}>
                <h5 style={{ margin: '0 0 6px', fontSize: 12, textTransform: 'uppercase',
                             letterSpacing: '.06em', color: 'var(--ink-3)' }}>
                  The finished cut
                </h5>
                {previewUrl ? (
                  <video src={previewUrl} controls ref={cutVideo}
                         style={{ width: '100%', borderRadius: 10, background: '#000' }} />
                ) : (
                  <button className="btn" disabled={busy === 'preview'}
                          onClick={() => void run('preview', async () => {
                            setPreviewUrl((await api.preview(id)).download.url);
                          })}>
                    {busy === 'preview' ? 'Fetching…' : 'Watch the finished video'}
                  </button>
                )}
              </div>
            )}
            </div>
          </div>

          {dirty && <p className="hint">Save your edits before rendering — the render uses what is stored.</p>}
          {d.job?.status === 'failed' && <div className="err">{d.job.error}</div>}

          <ThumbnailPanel
            card={d.card}
            rundown={d.rundown}
            selectedLine={selectedLine}
            assets={assets}
            onChanged={(card) => setD({ ...d, card })}
          />

          {/*
            Offered as a file rather than attached to the player below. A <track>
            would need the media bucket to send CORS headers for the studio's
            origin, and a <track> request carries no Authorization header anyway
            — so the version that always works is the one you can hand to
            anything. The bytes come through the API rather than a signed URL:
            navigating to a signed URL would leave a bearer credential in the
            address bar and in browser history.
          */}
          {d.job?.caption_key && (
            <p className="hint">
              <button
                className="linklike"
                disabled={busy === 'captions'}
                onClick={() => void run('captions', () => api.captions(id))}
              >
                Download captions (.vtt)
              </button>
              {' '}— written from the script and the measured length of each line, so they match the audio exactly.
            </p>
          )}
          {/* The sound alone, for a podcast feed (086): the mixed cut's audio
              as an MP3, written beside the video on every render. */}
          {d.job?.audio_key && (
            <p className="hint">
              <button
                className="linklike"
                disabled={busy === 'audio'}
                onClick={() => void run('audio', () => api.audio(id))}
              >
                Download audio (.mp3)
              </button>
              {' '}— the finished cut's sound, with the bed, the cues and the loudness pass, for a podcast feed.
            </p>
          )}

          {/*
            Before anything else in the publish half of the drawer: this is the
            one action that cannot be undone, and these are the failures that
            are otherwise found only after the bytes are up.
          */}
          {/* Said out loud. An unchecked publish is a decision the person should
              get to make knowingly, not one made for them by a silent catch. */}
          {preflightError && (
            <div className="err">
              The pre-publish check could not run — {preflightError}. Nothing below has
              been verified; publishing now skips the checks entirely.
            </div>
          )}
          {findings.length > 0 && (
            <ul className="preflight">
              {findings.map((f, i) => (
                <li key={i} className={f.severity}>
                  <span className="mark">{f.severity === 'blocker' ? 'Stops' : 'Note'}</span>
                  <span>
                    {f.says}
                    {f.fix && <em> — {f.fix}</em>}
                    {typeof f.at === 'number' && (
                      <button type="button" className="btn tiny" style={{ marginLeft: 6 }}
                              title={previewUrl ? 'Watch it in the finished cut' : 'Go to that scene on the timeline'}
                              onClick={() => {
                                // D6: a note can be watched. With the cut on screen it
                                // seeks; without, the timeline goes to that scene.
                                const v = cutVideo.current;
                                if (previewUrl && v) { v.currentTime = f.at!; void v.play(); v.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
                                else seek(f.at!);
                              }}>▶ {Math.floor(f.at / 60)}:{String(Math.floor(f.at % 60)).padStart(2, '0')}</button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* What it sounds like. Above the SEO pack and below the rundown,
              because it is the last thing that changes the FILE — everything
              after this point describes the file rather than altering it. */}
          <div data-tool="mix">
          <MixPanel
            card={d.card}
            onChange={(card) => setD({ ...d, card })}
            shelf={d.shelfTransitions ?? [...LEGACY_TRANSITIONS]}
            // Absent when the card has no brand: a shelf belongs to a brand,
            // and there is nowhere to keep one otherwise. MixPanel hides the
            // vault control rather than offering a button that saves nothing.
            onShelve={d.card.brand_id ? async (kind) => {
              const next = [...(d.shelfTransitions ?? LEGACY_TRANSITIONS)];
              if (!next.includes(kind)) next.push(kind);
              // Eight is the column's own ceiling. Dropping the OLDEST keeps
              // the shelf a working set rather than a queue that stops
              // accepting — a creator who picks a ninth means it.
              while (next.length > 8) next.splice(next.indexOf('cut') === 0 ? 1 : 0, 1);
              await api.saveBrandKit(d.card.brand_id!, { shelfTransitions: next });
              setD({ ...d, shelfTransitions: next });
            } : undefined}
          />
          </div>

          {/* What this publishes as. Above the shorts, because it is what goes
              out under the creator's name and the irreversible step reads it. */}
          <SeoPanel id={id} detail={d} onSaved={() => void load()} />

          {/*
            Shorts. Offered only once the long cut exists, because a short is a
            reframe of it rather than a thing of its own — the wording says so,
            so "Cut a short" never appears next to a card with nothing to cut.
          */}
          {d.job?.status === 'complete' && (
            <section className="shorts" data-tool="shorts">
              <h4>Shorts</h4>
              <p className="hint">
                A vertical cut for Reels, Shorts and TikTok. Nothing is cropped away —
                the picture keeps its full width over a blurred backdrop — and the
                captions are burned in, which is how these are watched.
              </p>
              {/*
                Pick the line, not the second. The render recorded where every
                line begins, so choosing a moment in the script is both easier
                and better than guessing at a number — a short that opens
                mid-word spends the half second a vertical feed gives it. The
                number stays for a render made before captions existed, which
                has no moments to offer.
              */}
              {shortPick && (
                <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }} data-tool="short-pick">
                  <span className="chip" title={shortPick.why}>
                    the director's pick · {Math.floor(shortPick.start / 60)}:{String(Math.floor(shortPick.start % 60)).padStart(2, '0')} · {Math.round(shortPick.duration)}s — {shortPick.why}
                  </span>
                  <button type="button" className="btn tiny pri" disabled={busy === 'short'}
                          title="Cut the forty-five seconds the director picked: the lines that name the subject, speak to the viewer and make the claim"
                          onClick={() => void run('short', async () => {
                            setShortStart(shortPick.start); setShortLen(Math.max(2, Math.round(shortPick.duration)));
                            await api.cutShort(id, shortPick.start, Math.max(2, Math.round(shortPick.duration)));
                            await load();
                          })}>
                    {busy === 'short' ? 'Queueing…' : "Cut the director's short"}
                  </button>
                  <button type="button" className="btn tiny" disabled={busy === 'short'} data-tool="cut-teaser"
                          title="A teaser at the video's own size: three moments the director picked, about eight seconds each, with an end card that names the episode — the trailer to post before it"
                          onClick={() => void run('short', async () => { await api.cutTeaser(id); await load(); })}>
                    {busy === 'short' ? 'Queueing…' : 'Cut a teaser'}
                  </button>
                  {shortPicks.length > 1 && (
                    <button type="button" className="btn tiny" disabled={busy === 'short'}
                            title={shortPicks.map((p) => `${Math.floor(p.start / 60)}:${String(Math.floor(p.start % 60)).padStart(2, '0')} · ${Math.round(p.duration)}s — ${p.why}`).join('\n')}
                            onClick={() => void run('short', async () => {
                              for (const p of shortPicks) await api.cutShort(id, p.start, Math.max(2, Math.round(p.duration)));
                              await load();
                            })}>
                      {busy === 'short' ? 'Queueing…' : `Cut ${shortPicks.length} shorts — a week of posts`}
                    </button>
                  )}
                </div>
              )}
              {moments.length > 0 && (
                <label className="from">
                  Start at
                  <select
                    value={String(shortStart)}
                    onChange={(e) => setShortStart(Number(e.target.value))}
                  >
                    {moments.map((m) => (
                      <option key={m.at} value={m.at}>
                        {Math.floor(m.at / 60)}:{String(Math.floor(m.at % 60)).padStart(2, '0')}
                        {' — '}
                        {m.text.length > 64 ? `${m.text.slice(0, 63)}…` : m.text}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="shortcut">
                {moments.length === 0 && (
                  <label>
                    Start
                    <input
                      type="number" min={0} step={1} value={shortStart}
                      onChange={(e) => setShortStart(Math.max(0, Number(e.target.value) || 0))}
                    />
                    <span className="unit">s</span>
                  </label>
                )}
                <div className="lens" role="group" aria-label="Length">
                  {[15, 30, 45, 60].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`chip${shortLen === n ? ' on' : ''}`}
                      aria-pressed={shortLen === n}
                      onClick={() => setShortLen(n)}
                    >{n}s</button>
                  ))}
                </div>
                <button
                  className="btn"
                  disabled={busy === 'short'}
                  onClick={() => void run('short', async () => {
                    await api.cutShort(id, shortStart, shortLen);
                    await load();
                  })}
                >{busy === 'short' ? 'Queueing…' : 'Cut a short'}</button>
              </div>

              {d.shorts?.length > 0 && (
                <ul className="shortlist">
                  {d.shorts.map((sh) => {
                    const from = Number(sh.start_seconds ?? 0);
                    const len = Number(sh.duration_seconds ?? 0);
                    return (
                      <li key={sh.id} data-kind={sh.kind ?? 'short'}>
                        <span className="range">
                          {sh.kind === 'teaser' ? 'teaser' : <>{Math.floor(from / 60)}:{String(Math.floor(from % 60)).padStart(2, '0')}{' + '}{len}s</>}
                        </span>
                        {sh.status === 'complete' ? (
                          <button
                            className="linklike"
                            onClick={() => void run(`short:${sh.id}`, async () => {
                              setShortUrl((await api.shortUrl(id, sh.id)).url);
                            })}
                          >Watch</button>
                        ) : sh.status === 'failed' ? (
                          <span className="err inline">{sh.error ?? 'failed'}</span>
                        ) : (
                          <span className="muted">{sh.status}…</span>
                        )}
                        {sh.status === 'complete' && sh.kind === 'teaser' && picked.length > 0 && (
                          /* S9: the teaser goes out the day before the episode, to the destinations picked below. */
                          <button type="button" className="btn tiny" data-tool="post-teaser" disabled={busy === 'schedule'}
                                  title={`Post the teaser the day before the episode (${picked.length} destination${picked.length === 1 ? '' : 's'} picked under Schedule)`}
                                  onClick={() => void run('schedule', async () => {
                                    const at = when ? new Date(new Date(when).getTime() - 86_400_000) : new Date(Date.now() + 3_600_000);
                                    await api.schedule(id, picked, at.toISOString(), { kind: 'teaser', jobId: sh.id });
                                    await load();
                                  })}>
                            {busy === 'schedule' ? 'Scheduling…' : 'Post the day before'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {shortUrl && (
                /* In the page, never a new tab: a signed URL is a bearer
                   credential and navigating to one leaves it in history. */
                <video
                  src={shortUrl} controls autoPlay
                  style={{ width: 240, borderRadius: 10, marginTop: 10, background: '#000' }}
                />
              )}
            </section>
          )}

        </section>

        {/* ── Distribute ── */}
        <section>
          <h4 data-tool="schedule">Schedule</h4>
          <div className="row wrap" style={{ gap: 8, alignItems: 'center', margin: '4px 0 10px' }} data-tool="on-rookery">
            <span className="sub-dim">On Rookery:</span>
            {(['public', 'members', 'unlisted'] as const).map((v) => (
              <button key={v} type="button" className={`chip${(d.card.visibility ?? 'public') === v ? ' on' : ''}`} aria-pressed={(d.card.visibility ?? 'public') === v}
                      title={v === 'public' ? 'On the door, in search, in the feed' : v === 'members' ? 'Plays for paying members and your team only' : 'Reachable by its link only'}
                      onClick={() => api.setCardVisibility(id, v).then(() => api.card(id)).then((next) => { setD(next); onChanged(next.card.stage as Stage); }).catch((e: Error) => setError(e.message))}>{v === 'members' ? 'members only' : v}</button>
            ))}
            {d.card.stage === 'published' && d.card.slug && <a href={`https://rookeryos.com/watch/${d.card.slug}/`} target="_blank" rel="noreferrer" className="sub-dim">rookeryos.com/watch/{d.card.slug}/ ↗</a>}
          </div>
          <SeriesRow cardId={id} brandId={d.card.brand_id} seriesId={d.card.series_id ?? null} seriesOrder={d.card.series_order ?? null}
                     onChanged={() => api.card(id).then((next) => setD(next)).catch((e: Error) => setError(e.message))} />
          {d.publications.length > 0 && (
            <table className="pubs">
              <tbody>
                {d.publications.map((p) => (
                  <tr key={p.id}>
                    <td><b>{p.platform}</b>{p.account_handle ? ` · ${p.account_handle}` : ''}</td>
                    <td style={{ color: p.status === 'published' ? 'var(--green)'
                                 : p.status === 'failed' ? 'var(--red)' : 'var(--ink-2)' }}>
                      {p.status}
                    </td>
                    <td style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                      {p.status === 'published' && p.stats && typeof p.stats.views === 'number'
                        ? <span title={p.stats_at ? `as the platform reported it ${new Date(p.stats_at).toLocaleString()}` : undefined}>
                            {p.stats.views.toLocaleString()} views · {(p.stats.likes ?? 0).toLocaleString()} likes · {(p.stats.comments ?? 0).toLocaleString()} comments
                          </span>
                        : p.scheduled_for ? new Date(p.scheduled_for).toLocaleString() : 'next run'}
                    </td>
                    <td className="r">
                      {p.remote_url
                        ? <a href={p.remote_url} target="_blank" rel="noreferrer">view</a>
                        : p.status !== 'published' && (
                            <button className="btn tiny" title="Remove this destination"
                                    onClick={() => void run('unschedule', () => api.unschedule(id, p.id))}>×</button>
                          )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {d.card.stage !== 'published' && (
            <div className="row wrap" style={{ gap: 8, alignItems: 'center', margin: '6px 0 10px' }} data-tool="publish-on-rookery">
              <button className="btn" disabled={Boolean(busy) || d.job?.status !== 'complete'}
                      title={d.job?.status === 'complete' ? 'The local copy goes to rookeryos.com now; the socials are separate' : 'Render the episode first'}
                      onClick={() => void run('schedule', async () => {
                        const r = await api.publishOnRookery(id);
                        setD({ ...d, card: r.card }); onChanged(r.card.stage as Stage);
                      })}>Publish on Rookery now</button>
              <span className="sub-dim">The local copy is the source of truth: it goes to the door whether or not it goes anywhere else.</span>
            </div>
          )}
          {connectors.length === 0 ? (
            <p className="hint">
              No social destinations yet. Add a channel under Connectors to schedule the socials; publishing on Rookery needs none.
            </p>
          ) : (
            <>
              <div className="dests">
                {connectors.map((c) => (
                  <label key={c.id} className={`dest${picked.includes(c.id) ? ' on' : ''}`}>
                    <input type="checkbox" checked={picked.includes(c.id)}
                           onChange={(e) => setPicked((v) =>
                             e.target.checked ? [...v, c.id] : v.filter((x) => x !== c.id))} />
                    <span>
                      <b>{c.platform}</b>
                      <i>{c.account_handle ?? c.brand_name}</i>
                    </span>
                    {!c.has_token && <span className="chip" title="This account is not connected yet">no token</span>}
                  </label>
                ))}
              </div>
              <div className="row wrap">
                <label className="inline">
                  When
                  <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
                </label>
                <button className="btn pri"
                        disabled={picked.length === 0 || Boolean(busy) || d.job?.status !== 'complete'}
                        onClick={() => void run('schedule', async () => {
                          await api.schedule(id, picked, when ? new Date(when).toISOString() : null);
                          setPicked([]);
                        })}>
                  {busy === 'schedule' ? 'Scheduling…' : when ? 'Schedule' : 'Publish at next run'}
                </button>
              </div>
              {d.job?.status !== 'complete' && (
                <p className="hint">A card can only be scheduled once its render has finished.</p>
              )}
            </>
          )}
        </section>

        {/* DELETE /v1/cards/:id has existed since the table did and had no
            caller, so cards only ever accumulated. It confirms first and names
            what goes with it — a hard delete with no undo that takes the script
            and the renders too — and it lives at the BOTTOM, because the header
            is the most-clicked corner of the screen and a destructive action
            does not belong next to Close. */}
        {d.card.stage === 'published' && <CardIndexPanel cardId={id} />}
        {d.card.stage === 'published' && <CardComments cardId={id} />}
        <section>
          <button className="btn danger-btn" onClick={() => void removeCard()}>Delete card</button>
        </section>
        </div>
      </div>
    </div>
  );
}

const PHASES: Array<{ stage: Stage; label: string; now: string }> = [
  { stage: 'idea',      label: 'Idea',      now: 'Gather what this episode will use, then write the script on it.' },
  { stage: 'scripted',  label: 'Scripted',  now: 'The script is on. Let the director assemble the timeline from the collection.' },
  { stage: 'filming',   label: 'Filming',   now: 'Film the lines that want you on camera; each take lands in its scene.' },
  { stage: 'editing',   label: 'Editing',   now: 'Lay it out and fine-tune on the timeline, then render.' },
  { stage: 'review',    label: 'Review',    now: 'Watch the cut. Fix, render again, or send it on to be scheduled.' },
  { stage: 'scheduled', label: 'Scheduled', now: 'A date, a destination — or a channel with a schedule for a live one.' },
  { stage: 'published', label: 'Published', now: 'Out. The next episode starts from the same show.' },
];

/** The phases (P8): where the card is, what to do now, and the click for it. */
function PhaseBar({ stage, dirty, rendering, hasLines, onMove, onGather, onWrite, onAssemble, onFilm, onRender, onSchedule, onRunOfShow, onClose }: {
  stage: Stage; dirty: boolean; rendering: boolean; hasLines: boolean;
  /** The way out. The editor covers the whole studio, so this sits top right of it. */
  onClose: () => void;
  onMove: (s: Stage) => void; onGather: () => void; onWrite: () => void; onAssemble: () => void;
  onFilm?: () => void; onRender: () => void; onSchedule: () => void;
  /** P10: the run of show as a file. */
  onRunOfShow?: () => void;
}) {
  const at = Math.max(0, PHASES.findIndex((p) => p.stage === stage));
  const phase = PHASES[at]!;
  return (
    <div className="phasebar" style={{ display: 'grid', gap: 6, padding: '8px 12px', borderBottom: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {PHASES.map((p, i) => (
          <button key={p.stage} type="button" className={`chip${i === at ? ' on' : ''}`}
                  style={{ opacity: i < at ? 0.7 : 1 }} title={i === at ? 'now' : `move the card to ${p.label}`}
                  onClick={() => { if (i !== at) onMove(p.stage); }}>
            {i < at ? '✓ ' : ''}{p.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn tiny" title="Back to the studio" onClick={onClose}>← Studio</button>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="sub-dim">Now: {phase.now}</span>
        <span style={{ flex: 1 }} />
        {stage === 'idea' && <><button className="btn tiny" onClick={onGather}>Gather</button><button className="btn tiny" onClick={() => document.querySelector('[data-tool="brief"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' })}>Brief</button><button className="btn tiny" onClick={onWrite}>Write the script</button></>}
        {stage === 'scripted' && <button className="btn tiny pri" disabled={!hasLines || dirty} title={dirty ? 'Save the script first' : ''} onClick={onAssemble}>Assemble the episode</button>}
        {stage === 'filming' && onFilm && <button className="btn tiny pri" disabled={!hasLines || dirty} onClick={onFilm}>Film the selected line</button>}
        {hasLines && onRunOfShow && (stage === 'scripted' || stage === 'filming' || stage === 'scheduled') && (
          <button className="btn tiny" disabled={dirty} title="Every scene with its time, who, the framing, the line, what is on screen and what is left to film — as a file the host holds" onClick={onRunOfShow}>Run of show</button>
        )}
        {(stage === 'editing' || stage === 'review') && <button className="btn tiny pri" disabled={!hasLines || dirty || rendering} title={dirty ? 'Save first' : rendering ? 'Rendering…' : ''} onClick={onRender}>{stage === 'review' ? 'Render again' : 'Render'}</button>}
        {stage === 'review' && <button className="btn tiny" onClick={onSchedule}>Ready to schedule</button>}
        {stage === 'scheduled' && <button className="btn tiny pri" onClick={onSchedule}>Schedule</button>}
      </div>
    </div>
  );
}

/** D14: the brief, edited in place; saved when the box loses focus. */
function BriefBox({ cardId, brief, busy, onChanged, onError }: { cardId: string; brief: string | null; busy: boolean; onChanged: (card: Card) => void; onError: (m: string | null) => void }) {
  const [text, setText] = useState(brief ?? '');
  const [writing, setWriting] = useState(false);
  useEffect(() => { setText(brief ?? ''); }, [brief]);
  async function ask() {
    setWriting(true); onError(null);
    try { const r = await api.writeBrief(cardId); setText(r.brief); onChanged(r.card); }
    catch (e) { onError((e as Error).message); }
    finally { setWriting(false); }
  }
  async function save() {
    const next = text.trim() || null;
    if ((brief ?? '') === (next ?? '')) return;
    try { onChanged(await api.saveBrief(cardId, next)); } catch (e) { onError((e as Error).message); }
  }
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <textarea rows={5} maxLength={2000} value={text} placeholder="Who it is for, what they get, why now — then three points to land."
                onChange={(e) => setText(e.target.value)} onBlur={() => void save()} style={{ width: '100%', resize: 'vertical' }} />
      <div className="row" style={{ gap: 6 }}>
        <button type="button" className="btn" disabled={busy || writing} onClick={() => void ask()}>
          {writing ? 'Writing…' : text.trim() ? 'Ask the director for another' : 'Ask the director for a brief'}
        </button>
        <span className="sub-dim">{text.length}/2000</span>
      </div>
    </div>
  );
}

/**
 * R9: the read-through. Every spoken line in its speaker's voice, one after
 * the other, before a render — the table read a producer hears for pace.
 * Each line comes from the same route the inspector's Hear this line uses,
 * so a local voice is heard too (T136). Stop at any time.
 */
function ReadThrough({ rundown, voices }: { rundown: Turn[]; voices: Record<string, string> | null }) {
  const [at, setAt] = useState<number | null>(null);
  const stop = useRef(false);
  const player = useRef<HTMLAudioElement | null>(null);
  const lines = rundown.map((t, i) => ({ t, i })).filter(({ t }) => t.kind !== 'title' && t.text.trim() && voices?.[t.speaker]);
  async function go() {
    stop.current = false;
    for (const { t, i } of lines) {
      if (stop.current) break;
      setAt(i);
      try {
        const url = await api.voiceSample(voices![t.speaker]!, t.read?.trim() || undefined, t.text);
        if (!url || stop.current) continue;
        await new Promise<void>((resolve) => {
          const a = new Audio(url); player.current = a;
          a.onended = () => { URL.revokeObjectURL(url); resolve(); };
          a.onerror = () => resolve();
          void a.play().catch(() => resolve());
        });
      } catch { /* a line that will not play is skipped; the read goes on */ }
    }
    setAt(null);
  }
  function halt() { stop.current = true; player.current?.pause(); setAt(null); }
  if (!lines.length) return null;
  return (
    <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 12 }}>
      {at === null
        ? <button type="button" className="btn tiny" title={`Hear every line in its speaker's voice, in order — ${lines.length} lines`} onClick={() => void go()}>▶ Read-through</button>
        : <><button type="button" className="btn tiny" onClick={halt}>■ Stop</button> <span className="sub-dim">line {at + 1} of {rundown.length}</span></>}
    </span>
  );
}
